/**
 * Enhanced Session Management
 * Provides session timeout, validation, and security features
 */

import { logger } from './logger';
import { supabase } from '../services/supabase';

class SessionManager {
  // Use environment variables for timeouts, with fallback defaults
  private static readonly SESSION_TIMEOUT = import.meta.env.VITE_SESSION_TIMEOUT_MINUTES 
    ? parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES) * 60 * 1000 
    : 15 * 60 * 1000; // Default: 15 minutes of inactivity
  
  private static readonly ABSOLUTE_TIMEOUT = import.meta.env.VITE_ABSOLUTE_TIMEOUT_HOURS
    ? parseInt(import.meta.env.VITE_ABSOLUTE_TIMEOUT_HOURS) * 60 * 60 * 1000
    : 8 * 60 * 60 * 1000; // Default: 8 hours absolute
    
  private static lastActivity = Date.now();
  private static sessionStart = Date.now();
  private static activityTimer: NodeJS.Timeout | null = null;

  /**
   * Initializes session monitoring
   */
  static init(): void {
    // Log configured timeouts
    logger.debug('[SessionManager] Initialized with timeouts:', {
      inactivityTimeout: `${this.SESSION_TIMEOUT / 1000 / 60} minutes`,
      absoluteTimeout: `${this.ABSOLUTE_TIMEOUT / 1000 / 60 / 60} hours`
    });
    
    // Check if we're on a public page
    const currentPath = window.location.pathname;
    const publicPaths = [
      '/', '/login', '/register', '/guest', '/public', '/how-it-works',
      '/features', '/about', '/contact', '/blog', '/pricing', '/invitation',
      '/privacy', '/terms', '/map-test'
    ];
    
    const isPublicPage = publicPaths.some(path => 
      currentPath === path || currentPath.startsWith(`${path}/`)
    );
    
    // Don't initialize session monitoring for public pages
    if (isPublicPage) {
      logger.debug('[SessionManager] Skipping session monitoring on public page');
      return;
    }

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), { passive: true });
    });

    // Start session validation timer
    this.startSessionMonitor();

    // Handle visibility change (tab focus)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.validateSession();
      }
    });
  }

  /**
   * Updates last activity timestamp
   */
  static updateActivity(): void {
    this.lastActivity = Date.now();
  }

  /**
   * Starts the session monitoring timer
   */
  private static startSessionMonitor(): void {
    // Clear existing timer
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
    }

    // Check session every minute
    this.activityTimer = setInterval(() => {
      this.validateSession();
    }, 60 * 1000);
  }

  /**
   * Checks if session has timed out
   */
  static isSessionExpired(): boolean {
    const inactivityTimeout = Date.now() - this.lastActivity > this.SESSION_TIMEOUT;
    const absoluteTimeout = Date.now() - this.sessionStart > this.ABSOLUTE_TIMEOUT;
    return inactivityTimeout || absoluteTimeout;
  }

  /**
   * Validates current session with comprehensive token verification
   */
  static async validateSession(): Promise<boolean> {
    try {
      // Check if we're on a public page that doesn't require authentication
      const currentPath = window.location.pathname;
      const publicPaths = [
        '/', '/login', '/register', '/guest', '/public', '/how-it-works',
        '/features', '/about', '/contact', '/blog', '/pricing', '/invitation',
        '/privacy', '/terms', '/map-test'
      ];
      
      const isPublicPage = publicPaths.some(path => 
        currentPath === path || currentPath.startsWith(`${path}/`)
      );
      
      // Skip validation entirely for public pages
      if (isPublicPage) {
        return true;
      }

      // Check for timeout
      if (this.isSessionExpired()) {
        logger.debug('[SessionManager] Session expired due to timeout');
        await this.logout('Session expired due to inactivity');
        return false;
      }

      // Check Supabase session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (session && !error) {
        // Valid Supabase session exists
        logger.debug('[SessionManager] Valid Supabase session found');
        return true;
      }

      // No valid session found
      logger.debug('[SessionManager] No valid Supabase session found');
      await this.logout('No valid authentication session');
      return false;
    } catch (error) {
      console.error('[SessionManager] Session validation error:', error);
      await this.logout('Session validation error');
      return false;
    }
  }

  /**
   * Refreshes the current session with Supabase
   */
  static async refreshSession(): Promise<boolean> {
    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        console.error('[SessionManager] Session refresh failed:', error);
        return false;
      }

      // Reset activity timer
      this.updateActivity();

      logger.debug('[SessionManager] Session refreshed successfully');
      return true;
    } catch (error) {
      console.error('[SessionManager] Session refresh error:', error);
      return false;
    }
  }

  /**
   * Logs out the user and cleans up session
   */
  static async logout(reason?: string): Promise<void> {
    try {
      logger.debug('[SessionManager] Logging out:', reason || 'User initiated');

      // Clear timer
      if (this.activityTimer) {
        clearInterval(this.activityTimer);
        this.activityTimer = null;
      }

      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear any remaining localStorage items
      const keysToRemove = [
        'vitly_access_token',
        'vitly_refresh_token',
        'vitly-supabase-auth'
      ];
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Redirect to login with reason
      const params = reason ? `?message=${encodeURIComponent(reason)}` : '';
      window.location.href = `/login${params}`;
    } catch (error) {
      console.error('[SessionManager] Logout error:', error);
      // Force redirect even on error
      window.location.href = '/login';
    }
  }

  /**
   * Resets the session start time (for fresh login)
   */
  static resetSession(): void {
    this.sessionStart = Date.now();
    this.lastActivity = Date.now();
  }

  /**
   * Gets session status information
   */
  static getSessionStatus(): {
    isActive: boolean;
    timeRemaining: number;
    lastActivity: Date;
    sessionAge: number;
  } {
    const now = Date.now();
    const inactivityTime = now - this.lastActivity;
    const sessionAge = now - this.sessionStart;
    const timeRemaining = Math.max(0, this.SESSION_TIMEOUT - inactivityTime);

    return {
      isActive: !this.isSessionExpired(),
      timeRemaining,
      lastActivity: new Date(this.lastActivity),
      sessionAge
    };
  }

  /**
   * Extends the session timeout (for important operations)
   */
  static extendSession(): void {
    this.updateActivity();
    logger.debug('[SessionManager] Session extended');
  }
}

export default SessionManager;